import { useMutation } from "@apollo/react-hooks";
import { ScheduleProvider } from "context";
import _ from "lodash";
import omit from "lodash/omit";
import { useMemo, useState } from "react";
import { currencyFormat, formatUpdateOrderInput } from "utils/utils";
import {
    Badge,
    Button,
    FormLayout,
    HeaderActions,
    Heading,
    LoadingProgress,
    Modal,
    Panel,
    Table,
    Text,
} from "@/ui";
import { useMeta, useNotification } from "@/hooks";
import { MUTATION_UPDATE_STORE } from "../../apollo/module-operations/store";
import styles from "./GeneralsForm.module.scss";
import deliveryStyles from "./DeliverySchedules.module.scss";
import ScheduleForm from "./ScheduleForm";
import SchedulesFormJSON from "./SchedulesFormJSON";
import {
    PlusIcon,
    PencilIcon,
    RowActionsMenu,
    storeTableUiStyles,
} from "./shared/index";

const DeliverySchedulesForm = ({ inputs, setInputs, loading }) => {
    const notify = useNotification();
    const { user } = useMeta();
    const [showForm, setShowForm] = useState(false);
    const [showFormJson, setShowFormJson] = useState(false);
    const [isCreate, setIsCreate] = useState(true);
    const [rowSelect, setRowSelect] = useState(null);
    const [isUpdating, setIsUpdating] = useState(false);
    const [isDelete, setIsDelete] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const [mutate] = useMutation(MUTATION_UPDATE_STORE, {
        refetchQueries: [],
        onCompleted: resp => {
            const input = _.cloneDeep(resp.updateStore);

            setInputs(input);
            handleCancel();
            setRowSelect(null);
            setShowDeleteConfirm(false);
            setIsDelete(false);
            notify(
                `Delivery Zone Was ${
                    isCreate ? "Created" : !isDelete ? "Updated" : "Removed"
                } Successfully"`,
                "success",
            );
        },
        onError: () => {
            setIsUpdating(false);
            notify(
                `Error ${
                    isCreate ? "Updating" : !isDelete ? "Creating" : "Removing"
                }  The Delivery Zone, If The Error Persist Please Contact Support.`,
                "error",
            );
        },
    });

    const handleCancel = () => {
        setIsUpdating(false);
        setShowForm(false);
        setShowFormJson(false);
    };

    const closeDeleteConfirm = () => {
        setShowDeleteConfirm(false);
        setIsDelete(false);
        setRowSelect(null);
    };

    const confirmDelete = () => {
        const newSchedules = _.filter(
            inputs.delivery_schedules,
            (_item, index) => index !== rowSelect,
        );

        const updateInputs = {
            ...omit(
                {
                    ...inputs,
                    delivery_schedules: newSchedules,
                },
                "ignore_whitespace",
            ),
        };

        setIsUpdating(true);

        mutate({
            variables: {
                account_id: user.accountId,
                store_id: user.storeId,
                input: formatUpdateOrderInput(updateInputs),
            },
        }).catch(() => {
            setIsUpdating(false);
        });
    };

    const handlerUpdatingOrCreate = array => {
        setIsUpdating(true);

        mutate({
            variables: {
                account_id: user.accountId,
                store_id: user.storeId,
                input: formatUpdateOrderInput({
                    ...omit(
                        {
                            ...inputs,
                            delivery_schedules: array,
                        },
                        "ignore_whitespace",
                    ),
                }),
            },
        });
    };

    const handlerUpdatingOrCreateJSON = nextInputs => {
        setIsUpdating(true);

        mutate({
            variables: {
                account_id: user.accountId,
                store_id: user.storeId,
                input: formatUpdateOrderInput(nextInputs),
            },
        });
    };

    const deliverySchedules = (inputs && inputs.delivery_schedules) || [];

    const rows = useMemo(
        () =>
            deliverySchedules.map((schedule, index) => ({
                ...schedule,
                _index: index,
            })),
        [deliverySchedules],
    );

    const columns = useMemo(
        () => [
            {
                id: "actions",
                header: "",
                width: 52,
                align: "center",
                renderCell: row => (
                    <span
                        className={storeTableUiStyles.rowActions}
                        onClick={event => event.stopPropagation()}
                    >
                        <RowActionsMenu
                            disabled={isUpdating || showForm || showFormJson}
                            label={row.name || `zone ${row._index + 1}`}
                            onDelete={() => {
                                setIsDelete(true);
                                setIsCreate(false);
                                setRowSelect(row._index);
                                setShowDeleteConfirm(true);
                            }}
                            onUpdate={() => {
                                setIsCreate(false);
                                setRowSelect(row._index);
                                setShowForm(true);
                            }}
                        />
                    </span>
                ),
            },
            {
                id: "name",
                header: "Name",
                minWidth: 160,
                renderCell: row => row.name || "",
            },
            {
                id: "express_menu_config",
                header: "Express Service",
                minWidth: 140,
                renderCell: row => (
                    <Badge
                        variant={
                            row.express_menu_config?.active
                                ? "primary"
                                : "neutral"
                        }
                    >
                        {row.express_menu_config?.active ? "Yes" : "No"}
                    </Badge>
                ),
            },
            {
                id: "min",
                header: "Minimum Cost",
                minWidth: 120,
                renderCell: row => currencyFormat(row.min),
            },
            {
                id: "free_after",
                header: "Free After",
                minWidth: 120,
                renderCell: row => currencyFormat(row.free_after),
            },
            {
                id: "delivery_fee",
                header: "Delivery Fee",
                minWidth: 120,
                renderCell: row => currencyFormat(row.delivery_fee),
            },
            {
                id: "stores_provider_id",
                header: "Store Provider",
                minWidth: 160,
                renderCell: row =>
                    row.stores_provider_id ? (
                        <Badge variant="primary">
                            {row.stores_provider_id}
                        </Badge>
                    ) : (
                        "—"
                    ),
            },
            {
                id: "geo_type",
                header: "Geo Type",
                minWidth: 120,
                renderCell: row => (
                    <Badge variant="secondary">{row.geo_type ?? "ZIP"}</Badge>
                ),
            },
        ],
        [isUpdating, showForm, showFormJson],
    );

    const toolbarDisabled = isUpdating || showFormJson || showForm;

    return (
        <>
            <Modal
                description="Are you sure you want to remove this delivery zone?"
                footer={
                    <div className={deliveryStyles.modalActions}>
                        <Button
                            loading={isUpdating}
                            onClick={confirmDelete}
                            type="button"
                        >
                            Confirm
                        </Button>
                        <Button
                            disabled={isUpdating}
                            onClick={closeDeleteConfirm}
                            type="button"
                            variant="secondary"
                        >
                            Cancel
                        </Button>
                    </div>
                }
                onOpenChange={open => {
                    if (!open) closeDeleteConfirm();
                }}
                open={showDeleteConfirm}
                title="Remove delivery zone"
            >
                {null}
            </Modal>

            <Panel
                className={styles.root}
                header={
                    <div className={styles.headerBar}>
                        <div className={styles.headerText}>
                            <Heading level={4}>Delivery Schedules</Heading>
                            <Text tone="secondary" variant="body-md">
                                Delivery Schedules Information
                            </Text>
                        </div>
                        <HeaderActions
                            actions={[
                                {
                                    id: "add",
                                    label: "Add Delivery Zone",
                                    icon: <PlusIcon />,
                                    variant: "secondary",
                                    disabled: toolbarDisabled,
                                    onClick: () => {
                                        setIsCreate(true);
                                        setShowForm(true);
                                    },
                                },
                                {
                                    id: "json",
                                    label: "Update Form JSON",
                                    icon: <PencilIcon />,
                                    disabled: toolbarDisabled,
                                    onClick: () => {
                                        setIsCreate(true);
                                        setShowFormJson(true);
                                    },
                                },
                            ]}
                            aria-label="Delivery schedule actions"
                        />
                    </div>
                }
                showHeaderDivider
            >
                {showForm ? (
                    <ScheduleProvider
                        contextType="Delivery"
                        defaultCoordinates={{
                            lat: inputs?.latitude,
                            lng: inputs?.longitude,
                        }}
                        handlerUpdatingOrCreate={handlerUpdatingOrCreate}
                        isCreate={isCreate}
                        isUpdating={isUpdating}
                        schedules={inputs.delivery_schedules}
                        selectSchedule={rowSelect}
                    >
                        <ScheduleForm handleCancel={handleCancel} />
                    </ScheduleProvider>
                ) : null}

                {showFormJson ? (
                    <SchedulesFormJSON
                        context="Delivery"
                        handleCancel={handleCancel}
                        handlerUpdatingOrCreate={handlerUpdatingOrCreateJSON}
                        inputs={inputs}
                        isUpdating={isUpdating}
                        setInputs={setInputs}
                    />
                ) : null}

                <FormLayout>
                    <FormLayout.Section title="Delivery Zones">
                        {loading ? (
                            <div className={styles.loading}>
                                <LoadingProgress
                                    indeterminate
                                    indicator="circular"
                                    size="sm"
                                    title="Loading"
                                />
                            </div>
                        ) : (
                            <Table
                                columns={columns}
                                getRowId={row => String(row._index)}
                                rows={rows}
                            />
                        )}
                    </FormLayout.Section>
                </FormLayout>
            </Panel>
        </>
    );
};

export default DeliverySchedulesForm;
